import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, Rating, IconButton, colors, Color, Container, Grid, Modal } from '@mui/material';
import { useDrawingArea } from '@mui/x-charts/hooks';
import CustomDataGrid from 'utils/customDataGrid';
import { AuthContext } from 'utils/AuthContext';
import CommentIcon from '@mui/icons-material/Comment';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import Paper from '@mui/material/Paper';
import { mwiseRating } from 'store/slices/adminSlice';
import { useAppDispatch } from 'store/hooks';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import COLORS from 'theme/colors';
import { render } from '@testing-library/react';
import { DateFilter, DateTimeFilterOperators } from 'utils/gridOperators';
import { formatDate } from 'utils/dataFormatter';
import dayjs from 'dayjs';

export default function RatingsDashboard() {
  const [ratingData, setRatingData] = useState<any[] | null>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { user, setUserDetails } = useContext(AuthContext);
  const [openModal, setOpenModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState('');
  const [allAssessmentNumber, setAllAssessmentNumber] = useState(0);
  
  const dispatch = useAppDispatch();

  const getMwiseRating = () => {
    dispatch(mwiseRating({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setRatingData(response?.data);
          setLoading(false);
          setAllAssessmentNumber(response?.data[0].allAssessmentNumber);
        }
      });
  };

  //Good greater 12.1 
  function countGoodScore(data: any[]) {
    return data.filter((item) => item.averageScore >= 12.1).length;
  }

  //Average 8.1  between 12.1
  function countAverageScore(data: any[]) {
    return data.filter((item) => item.averageScore >= 8.1 && item.averageScore < 12.1).length;
  }

   //Poor 1  to 8
  function countBadScore(data: any[]) {
    return data.filter((item) => item.averageScore >= 1 && item.averageScore <=8).length;
  }
  // No score
  function countNoScore(data: any[]) {
    return data.filter((item) => item.averageScore === null || item.averageScore === undefined).length;
  }

  function getQuiz1Sum(data: any) {
    return data.reduce((sum: any, item: { quiz1: any }) => sum + (item.quiz1 || 0), 0);
  }
  function getQuiz2Sum(data: any) {
    return data.reduce((sum: any, item: { quiz2: any }) => sum + (item.quiz2 || 0), 0);
  }
  function getQuiz3Sum(data: any) {
    return data.reduce((sum: any, item: { quiz3: any }) => sum + (item.quiz3 || 0), 0);
  }
  const dataset = ratingData
    ? [
        {
          quiz: 'Question 1',
          score: getQuiz1Sum(ratingData) / ratingData.length,
        },
        {
          quiz: 'Question 2',
          score: getQuiz2Sum(ratingData) / ratingData.length,
        },
        {
          quiz: 'Question 3',
          score: getQuiz3Sum(ratingData) / ratingData.length,
        },
      ]
    : [];

  // Custom colors for each bar

  useEffect(() => {
    getMwiseRating();
  }, [dispatch, user?.userId]);

  const handleOpenComments = (comment: string) => {
    setSelectedComment(comment);
    setOpenModal(true);
  };

  const columns = [
    {
      field: 'date',
      headerName: 'Date',
      width: 100,
      type:'date',
      sortable: true,
      filterable: true,
      filterOperators: DateTimeFilterOperators,
      valueFormatter: (params: any) => formatDate(params?.value),
      valueGetter: (params: any) => params?.value,
      renderCell: (params: any) => {
        return params?.value ? (
          <>
            {' '}
            {dayjs
              .utc(params?.value)
              .local()
              .format('MM/DD/YYYY')
              .substring(0, 10)}
          </>
        ) : (
          ''
        );
      },
    },
    { field: 'patientName', headerName: 'Patient Name', flex: 1, width: 250, hideable: false },
    {
      field: 'quiz1',
      headerName: 'Question 1',
      width: 190,
      hideable: false,
      renderCell: (params: any) => {
        return params?.value ? 
            <Rating value={params.value} readOnly />
        : null;
      },
    },

    {
      field: 'quiz2',
      headerName: 'Question 2',
      width: 190,
      hideable: false,
      renderCell: (params: any) => {
        return params?.value ? 
            <Rating value={params.value} readOnly />
        : null;
      },
    },
    {
      field: 'quiz3',
      headerName: 'Question 3',
      width: 190,
      hideable: false,
      renderCell: (params: any) => {
        return params?.value ? (
            <Rating value={params.value} readOnly />
        ) : null;
      },
    },
    {
      field: 'averageScore',
      headerName: 'Average Score',
      width: 100,
      renderCell: (params: any) => {
        return params.row.averageScore ? (
          <>
            <SentimentSatisfiedAltIcon
              color={
                params.row.averageScore < 3
                  ? 'error'
                  : params.row.averageScore <= 6
                  ? 'error'
                  : params.row.averageScore < 9
                  ? 'warning'
                  : 'success'
              }
            />
            {'    ' + params.row.averageScore}
          </>
        ) : null;
      },
    },
    {
      field: 'comments',
      headerName: 'Comments',
      width: 200,
      renderCell: (params: any) => {
        return params?.value ? (
          <IconButton
            color='primary'
            onClick={() => handleOpenComments(params.value)}
            aria-label='view comments'
            size='small'
          >
            <CommentIcon />
            {params.value}
          </IconButton>
        ) : null;
      },
    },
     
  ];

  return (
    <>
      <Container maxWidth='lg'>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mt: 4,
            mb: 3,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          {/* -------------------- PAPER 1 -------------------- */}
          <Paper
            elevation={10}
            sx={{
              p: 2,
              borderRadius: 3,
              mb: 2
            }}
          >
            {/* ----------- HEADER ----------- */}
            <Box sx={{ mb: 2 }}>
              <Typography variant='h6' sx={{ mb: 2, fontWeight: 550 }}>
                Question-wise Average Ratings
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Average score (1–5) based on patient feedback
              </Typography>
            </Box>

            {/* ----------- CHART ----------- */}
            <BarChart
              layout='horizontal'
              height={200}
              margin={{ left: 100 }}
              dataset={dataset}
              xAxis={[
                {
                  min: 0,
                  max: 5,
                },
              ]}
              yAxis={[{ scaleType: 'band', dataKey: 'quiz' }]}
              series={[
                {
                  dataKey: 'score',
                  label: 'M-Wise Rating Scale (1-5)',
                },
              ]}
              sx={{
                '& .MuiBarElement-root': {
                  rx: 10,
                  fill: 'rgb(2, 178, 175)',
                },
                '& .MuiChartsAxis-tickLabel': {
                  fontSize: 13,
                  fontWeight: 500,
                },
              }}
            />

            {/* ----------- QUIZ INFO CARDS ----------- */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 2,
                mt: 3,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: '#e3f2fd',
                }}
              >
                <Typography fontWeight={600}>Question 1:</Typography>
                <Typography variant='body1' color='text.secondary'>
                  How well did the questions resonate with your concerns?
                </Typography>
              </Paper>

              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: '#e3f2fd',
                }}
              >
                <Typography fontWeight={600}>Question 2:</Typography>
                <Typography variant='body1' color='text.secondary'>
                  How seamless was your experience with the questionnaire?
                </Typography>
              </Paper>

              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: '#e3f2fd',
                }}
              >
                <Typography fontWeight={600}>Question 3:</Typography>
                <Typography variant='body1' color='text.secondary'>
                  How satisfied are you with your overall experience?
                </Typography>
              </Paper>
            </Box>
          </Paper>

          {/* -------------------- PAPER 2 -------------------- */}
          {/* -------------------- PAPER 2 (Big Card with 4 Mini Cards) -------------------- */}
          <Paper
            elevation={6}
            sx={{
              flex: 1,
              minWidth: 400,
              p: 2,
              borderRadius: 3,
            }}
          >
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 550 }}>
              Overall Patient M-Wise Feedback Summary
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              {/* CARD 1 */}

              {/* CARD 2 */}
              <Paper
                elevation={3}
                sx={{
                  flex: 1,
                  minWidth: 140,
                  p: 2,
                  textAlign: 'center',
                  borderRadius: 3,
                  bgcolor: '#e3f2fd',
                }}
              >
                <Typography variant='subtitle1' fontWeight={600}>
                  Average Score
                </Typography>
                <Typography variant='h5' fontWeight={700} color={'#ed6c02'}>
                  {countAverageScore(ratingData || [])}
                </Typography>
              </Paper>
              <Paper
                elevation={3}
                sx={{
                  flex: 1,
                  minWidth: 140,
                  p: 2,
                  textAlign: 'center',
                  borderRadius: 3,
                  bgcolor: '#e3f2fd', // light blue – same theme
                }}
              >
                <Typography variant='subtitle1' fontWeight={600}>
                  Good Score
                </Typography>
                <Typography variant='h5' fontWeight={700} color={'#2e7d32'}>
                  {countGoodScore(ratingData || [])}
                </Typography>
              </Paper>
              {/* CARD 3 */}
              <Paper
                elevation={3}
                sx={{
                  flex: 1,
                  minWidth: 140,
                  p: 2,
                  textAlign: 'center',
                  borderRadius: 3,
                  bgcolor: '#e3f2fd',
                }}
              >
                <Typography variant='subtitle1' fontWeight={600}>
                  Poor Score
                </Typography>
                <Typography variant='h5' fontWeight={700} color={'#d32f2f'}>
                  {countBadScore(ratingData || [])}
                </Typography>
              </Paper>
              {/* CARD 3 */}
              <Paper
                elevation={3}
                sx={{
                  flex: 1,
                  minWidth: 140,
                  p: 2,
                  textAlign: 'center',
                  borderRadius: 3,
                  bgcolor: '#e3f2fd',
                }}
              >
                <Typography variant='subtitle1' fontWeight={600}>
                  Feedback Pending
                </Typography>
                <Typography variant='h5' fontWeight={700} color={'#504e4eff'}>
                  {(allAssessmentNumber - (ratingData?.length|| 0))}
                </Typography>
              </Paper>
              {/* CARD 4 */}
              <Paper
                elevation={3}
                sx={{
                  flex: 1,
                  minWidth: 140,
                  p: 2,
                  textAlign: 'center',
                  borderRadius: 3,
                  bgcolor: '#e3f2fd',
                }}
              >
                <Typography variant='subtitle1' fontWeight={600}>
                  Total Patients
                </Typography>
                <Typography variant='h5' fontWeight={700}>
                  {ratingData?.length || 0}
                </Typography>
              </Paper>
            </Box>
          </Paper>
        </Box>
      </Container>
      <Container maxWidth='lg'>
        <Grid xs={12}>
          <Typography
            variant='h5'
            position={'relative'}
            width={'fit-content'}
            sx={{ paddingTop: 6, textTransform: 'capitalize' }}
          >
            M-Wise Rating & Feedback Dashboard
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr' } }}>
            <CustomDataGrid rows={ratingData ? ratingData : []} columns={columns} dashBordKey={'ratings_dashboard'} />
          </Box>
        </Grid>
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              width: 400,
            }}
          >
            <Typography variant='h6' mb={2}>
              M-Wise Feedback Comment
            </Typography>

            <Typography variant='body1' sx={{ whiteSpace: 'pre-line' }}>
              {selectedComment}
            </Typography>
          </Box>
        </Modal>
      </Container>
    </>
  );
}
